import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LuCompass,
  LuSearch,
  LuChevronRight,
  LuInfo,
  LuChurch,
  LuSchool,
  LuLayers,
  LuBookOpen,
  LuVideo,
  LuMail,
  LuUsers,
  LuHeartHandshake,
  LuArrowUpRight,
  } from 'react-icons/lu';
import { PiChurchLight } from 'react-icons/pi';
import heroBg from '../../assets/about_cathedral_wide.jpg';

export default function Sitemap({ activeSection = 'sitemap', onSelectSection = () => {} }) {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');

  const handleNavigate = (path, sectionKey) => {
    if (path) {
      navigate(path);
    } else if (sectionKey) {
      onSelectSection(sectionKey);
    }
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Comprehensive site structure database
  const sitemapData = [
    {
      category: 'Main Pages & Overview',
      icon: LuCompass,
      color: 'from-amber-600 to-amber-800',
      badgeColor: 'bg-amber-100 text-amber-900 border-amber-300',
      description: 'Primary entry points to learn about the CSI Malabar Diocese.',
      links: [
        { label: 'Home Page', path: '/home', desc: 'Welcome to the official portal of CSI Malabar Diocese' },
        { label: 'About Diocese', path: '/about-diocese', desc: 'Our vision, mission, governance, and history' },
        { label: 'Diocesan History', path: '/history', desc: 'Rich heritage, milestones, and historical legacy' },
        { label: "Bishop's Office", path: '/bishop', desc: 'Message from the Bishop, administration & leadership' },
        { label: 'Diocesan Administration', path: '/council', desc: 'Diocesan Council, Officers, and Executive Executive Body' },
        { label: 'Lights of Diocese', path: '/lights', desc: 'Clergy, presbyters, evangelists, and ministry workers' },
        { label: 'Former Bishops', path: '/former-bishops', desc: 'Archival roll of honored former Bishops' },
        { label: 'Contact Us', path: '/contact', desc: 'Get in touch, address, map location, and inquiries' },
      ]
    },
    {
      category: 'Parishes & Churches',
      icon: LuChurch,
      color: 'from-emerald-600 to-emerald-800',
      badgeColor: 'bg-emerald-100 text-emerald-900 border-emerald-300',
      description: 'Explore parishes, councils, and worship centers across Malabar.',
      links: [
        { label: 'All Parishes & Churches', path: '/churches', desc: 'Complete directory of CSI churches across districts' },
        { label: 'Search Churches', path: '/churches', desc: 'Locate local parishes by area, district, or name' },
        { label: 'District Councils', path: '/churches', desc: 'Parish coverage in Kozhikode, Kannur, Wayanad, etc.' },
      ]
    },
    {
      category: 'Educational Institutions',
      icon: LuSchool,
      color: 'from-blue-600 to-blue-800',
      badgeColor: 'bg-blue-100 text-blue-900 border-blue-300',
      description: 'Diocesan Colleges, Higher Secondary, High Schools, and Primary Schools.',
      links: [
        { label: 'All Institutions Overview', path: '/institutions', desc: 'Comprehensive list of educational and social bodies' },
        { label: 'Colleges', path: '/colleges', desc: 'Higher education institutions and arts & science colleges' },
        { label: 'Schools Overview', path: '/schools', desc: 'Aided and unaided school networks' },
        { label: 'Aided Higher Secondary', path: '/aided-higher-secondary', desc: 'Government-aided higher secondary institutions' },
        { label: 'Aided High Schools', path: '/aided-high-schools', desc: 'Aided secondary high schools' },
        { label: 'Aided UP Schools', path: '/aided-up-schools', desc: 'Aided Upper Primary education' },
        { label: 'Aided LP Schools', path: '/aided-lp-schools', desc: 'Aided Lower Primary education' },
        { label: 'Unaided Higher Secondary', path: '/unaided-higher-secondary', desc: 'Self-financing higher secondary schools' },
        { label: 'Unaided High Schools', path: '/unaided-high-schools', desc: 'Self-financing high schools' },
        { label: 'Unaided UP Schools', path: '/unaided-up-schools', desc: 'Unaided Upper Primary schools' },
        { label: 'Unaided LP Schools', path: '/unaided-lp-schools', desc: 'Unaided Lower Primary schools' },
      ]
    },
    {
      category: 'Medical & Social Care',
      icon: LuHeartHandshake,
      color: 'from-rose-600 to-rose-800',
      badgeColor: 'bg-rose-100 text-rose-900 border-rose-300',
      description: 'Hospitals, retreat centers, child care, and community outreach.',
      links: [
        { label: 'CSI Mission Hospitals', path: '/hospitals', desc: 'Healthcare ministries and medical facilities' },
        { label: 'Santi Bhavan Retreat Centre', path: '/retreat', desc: 'Spiritual renewal, retreats, and meditation center' },
        { label: 'Social Service Board', path: '/socialboard', desc: 'Community welfare, empowerment, and outreach programs' },
        { label: 'Child Care Projects', path: '/childcare', desc: 'Development initiatives for children and youth' },
        { label: 'Diocesan Book Shop', path: '/bookshop', desc: 'Bibles, liturgical books, literature, and supplies' },
      ]
    },
    {
      category: 'Departments & Ministries',
      icon: LuLayers,
      color: 'from-purple-600 to-purple-800',
      badgeColor: 'bg-purple-100 text-purple-900 border-purple-300',
      description: 'Active ministry boards, fellowships, and youth organizations.',
      links: [
        { label: 'All Departments Overview', path: '/departments', desc: 'Explore all active diocesan ministry boards' },
        { label: "Women's Fellowship", path: '/departments', desc: 'Empowering women through spiritual growth and service' },
        { label: 'Youth Department', path: '/departments', desc: 'Engaging young believers in faith and leadership' },
        { label: 'Sunday School Department', path: '/departments', desc: 'Nurturing children in biblical foundation' },
        { label: 'Gospel & Mission Board', path: '/departments', desc: 'Evangelism and mission initiatives across regions' },
      ]
    },
    {
      category: 'Publications & Daily Worship',
      icon: LuBookOpen,
      color: 'from-amber-700 to-orange-900',
      badgeColor: 'bg-amber-100 text-amber-900 border-amber-300',
      description: 'Literary publications, magazines, and daily lectionary readings.',
      links: [
        { label: 'Publications & Literature', path: '/publications', desc: 'Diocesan newsletters, journals, and books' },
        { label: 'Daily Lectionary & Readings', path: '/readings', desc: 'Daily Scripture readings, liturgical calendar, and devotionals' },
        { label: 'Diocesan News', path: '/news', desc: 'Latest updates, announcements, and press releases' },
        { label: 'Events Calendar', path: '/events', desc: 'Upcoming diocesan conventions and special services' },
      ]
    },
    {
      category: 'Media & Communications',
      icon: LuVideo,
      color: 'from-red-600 to-red-800',
      badgeColor: 'bg-red-100 text-red-900 border-red-300',
      description: 'Live service streams, photo archives, and video content.',
      links: [
        { label: 'Live Streaming', path: '/live', desc: 'Watch Sunday services, conventions, and live broadcasts' },
        { label: 'Photo & Video Gallery', path: '/gallery', desc: 'Visual records of diocesan gatherings and celebrations' },
      ]
    },
    {
      category: 'Lights of Diocese (Clergy Directory)',
      icon: LuUsers,
      color: 'from-cyan-700 to-blue-900',
      badgeColor: 'bg-cyan-100 text-cyan-900 border-cyan-300',
      description: 'Dedicated servants of God serving across the Malabar Diocese.',
      links: [
        { label: 'Active Presbyters', path: '/presbyters', desc: 'Ordained ministers serving in parishes' },
        { label: 'Deacons', path: '/deacons', desc: 'Deacons engaged in pastoral care' },
        { label: 'Evangelists', path: '/evangelists', desc: 'Evangelists in active gospel ministry' },
        { label: 'Church Workers', path: '/church-workers', desc: 'Lay workers supporting diocesan institutions' },
        { label: 'Presbyters on Study', path: '/presbyters-on-study', desc: 'Clergy pursuing higher theological studies' },
        { label: 'Seminary Students', path: '/seminary-students', desc: 'Ordinands currently in seminary training' },
        { label: 'Deputation Ministry', path: '/deputation', desc: 'Clergy serving on external deputation' },
        { label: 'Missionaries', path: '/missionaries', desc: 'Missionaries serving in regional fields' },
        { label: 'Retired Presbyters', path: '/retired-presbyters', desc: 'Honored retired ministers' },
        { label: 'Retired Evangelists', path: '/retired-evangelists', desc: 'Retired evangelists directory' },
      ]
    }
  ];

  // Filter links based on search query
  const filteredData = sitemapData.map(group => {
    const matchedLinks = group.links.filter(
      link =>
        link.label.toLowerCase().includes(searchQuery.toLowerCase()) ||
        link.desc.toLowerCase().includes(searchQuery.toLowerCase()) ||
        group.category.toLowerCase().includes(searchQuery.toLowerCase())
    );
    return { ...group, links: matchedLinks };
  }).filter(group => group.links.length > 0);

  const totalFilteredLinks = filteredData.reduce((sum, g) => sum + g.links.length, 0);

  return (
    <div className="bg-[#fcfbf9] min-h-screen text-slate-800 pt-20 pb-16">

      {/* Hero Header */}
      <section className="relative bg-[#1a0e26] text-white py-16 sm:py-20 overflow-hidden">
        <div className="absolute inset-0 z-0 opacity-25">
          <img
            src={heroBg}
            alt="Diocese Cathedral"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1a0e26] via-[#1a0e26]/80 to-transparent"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/10 text-[#f3e5ab] backdrop-blur-md text-xs font-semibold uppercase tracking-widest border border-white/20 mb-4">
            <LuCompass className="w-4 h-4 text-[#e5c98b]" />
            Website Sitemap & Navigation Guide
          </div>

          <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl font-extrabold tracking-tight text-white mb-4">
            Explore <span className="text-[#e5c98b]">CSI Malabar</span> Diocese
          </h1>

          <p className="text-white/80 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed mb-8 font-light">
            Quickly navigate to any page, parish directory, educational institution, ministry department, or publication across our website.
          </p>

          {/* Search Box */}
          <div className="max-w-xl mx-auto relative">
            <div className="relative flex items-center">
              <LuSearch className="absolute left-4 w-5 h-5 text-slate-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search sitemap links (e.g., Schools, Readings, Presbyters)..."
                className="w-full pl-12 pr-4 py-3.5 bg-white text-slate-900 rounded-xl shadow-lg border border-white/20 focus:outline-none focus:ring-2 focus:ring-[#e5c98b] text-sm sm:text-base transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-4 text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded hover:bg-slate-300"
                >
                  Clear
                </button>
              )}
            </div>
            <p className="text-xs text-white/60 mt-2">
              Showing {totalFilteredLinks} navigation destinations
            </p>
          </div>
        </div>
      </section>

      {/* Main Sitemap Content */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {filteredData.length === 0 ? (
          <div className="bg-white rounded-2xl p-12 text-center shadow-sm border border-slate-200 max-w-md mx-auto">
            <LuInfo className="w-12 h-12 text-amber-500 mx-auto mb-3" />
            <h3 className="font-serif font-bold text-lg text-slate-900 mb-1">No pages found</h3>
            <p className="text-sm text-slate-600 mb-4">
              We couldn't find any page matching "{searchQuery}". Try a different keyword like "Schools", "Bishop", or "Churches".
            </p>
            <button
              onClick={() => setSearchQuery('')}
              className="px-4 py-2 bg-[#842343] text-white text-sm font-medium rounded-lg hover:bg-[#6b1c36] transition-colors"
            >
              Reset Search
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredData.map((group, groupIdx) => {
              const IconComp = group.icon;
              return (
                <div
                  key={groupIdx}
                  className="bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-md transition-shadow duration-300 overflow-hidden flex flex-col"
                >
                  {/* Category Card Header */}
                  <div className={`p-5 bg-gradient-to-r ${group.color} text-white relative`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="p-2.5 rounded-xl bg-white/10 backdrop-blur-sm border border-white/20">
                          <IconComp className="w-6 h-6 text-white" />
                        </div>
                        <h2 className="font-serif font-bold text-lg text-white leading-snug">
                          {group.category}
                        </h2>
                      </div>
                      <span className="text-xs px-2.5 py-1 rounded-full font-semibold bg-white/20 text-white backdrop-blur-sm border border-white/20">
                        {group.links.length} {group.links.length === 1 ? 'Link' : 'Links'}
                      </span>
                    </div>
                    <p className="text-xs text-white/80 mt-2.5 font-light leading-relaxed">
                      {group.description}
                    </p>
                  </div>

                  {/* Links List */}
                  <div className="p-4 flex-1 divide-y divide-slate-100">
                    {group.links.map((link, linkIdx) => (
                      <div
                        key={linkIdx}
                        onClick={() => handleNavigate(link.path)}
                        className="py-3 px-2 rounded-xl hover:bg-slate-50 transition-colors cursor-pointer group flex items-start justify-between gap-3"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-1.5 font-semibold text-slate-900 text-sm group-hover:text-[#842343] transition-colors">
                            <span>{link.label}</span>
                            <LuArrowUpRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity text-[#842343]" />
                          </div>
                          <p className="text-xs text-slate-500 mt-0.5 leading-snug font-normal">
                            {link.desc}
                          </p>
                        </div>
                        <LuChevronRight className="w-4 h-4 text-slate-300 group-hover:text-[#842343] group-hover:translate-x-1 transition-all mt-1 shrink-0" />
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Quick Help & Contact Bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-6">
        <div className="bg-gradient-to-r from-[#842343] to-[#5a162d] rounded-2xl p-8 text-white shadow-xl flex flex-col md:flex-row items-center justify-between gap-6 border border-[#a12f55]">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-white/10 flex items-center justify-center border border-white/20 shrink-0">
              <PiChurchLight className="w-8 h-8 text-[#e5c98b]" />
            </div>
            <div>
              <h3 className="font-serif font-bold text-xl text-white">Need Assistance Finding Information?</h3>
              <p className="text-white/80 text-sm mt-1 max-w-xl font-light">
                Our team at the CSI Malabar Diocesan Headquarters is happy to help guide you. Contact us directly for any inquiries.
              </p>
            </div>
          </div>
          <button
            onClick={() => handleNavigate('/contact')}
            className="px-6 py-3 bg-[#e5c98b] text-[#842343] font-bold rounded-xl hover:bg-white transition-all shadow-md hover:shadow-lg whitespace-nowrap text-sm flex items-center gap-2"
          >
            <LuMail className="w-4 h-4" />
            Contact Diocesan Office
          </button>
        </div>
      </section>

    </div>
  );
}
